import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Upload,
  FileText,
  FileType2,
  File,
  X,
  CheckCircle2,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useUploadSource } from '@/features/sources/hooks/useSources';
import { toast } from 'sonner';
import type { UploadProgress } from '@/features/sources/types';

interface SourceUploaderProps {
  folderId?: string | null;
  onUploadComplete?: () => void;
  maxFiles?: number;
  maxSizeMb?: number;
  className?: string;
}

const ACCEPTED_TYPES: Record<string, string[]> = {
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'text/plain': ['.txt'],
};

const fileIconMap: Record<string, { icon: typeof FileText; color: string }> = {
  'application/pdf': { icon: FileText, color: 'text-rose-500' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {
    icon: FileType2,
    color: 'text-blue-500',
  },
  'text/plain': { icon: File, color: 'text-slate-500' },
};


export function SourceUploader({
  folderId = null,
  onUploadComplete,
  maxFiles = 20,
  maxSizeMb = 50,
  className,
}: SourceUploaderProps) {
  const [queue, setQueue] = useState<UploadProgress[]>([]);
  const uploadSource = useUploadSource();

  const isUploading = queue.some((item) => item.status === 'uploading' || item.status === 'extracting');

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      const newItems: UploadProgress[] = acceptedFiles.map((file) => ({
        source_id: '',
        file_name: file.name,
        progress: 0,
        status: 'uploading' as const,
        error: null,
      }));

      // Capture the base index from the current queue length via the updater
      let baseIndex = 0;
      setQueue((prev) => {
        baseIndex = prev.length;
        return [...prev, ...newItems];
      });

      for (let i = 0; i < acceptedFiles.length; i++) {
        const file = acceptedFiles[i] as File;
        const queueIndex = baseIndex + i;

        try {
          setQueue((prev) =>
            prev.map((item, idx) =>
              idx === queueIndex ? { ...item, progress: 30, status: 'uploading' } : item
            )
          );

          const source = await uploadSource.mutateAsync({
            file,
            folder_id: folderId ?? null,
          });

          setQueue((prev) =>
            prev.map((item, idx) =>
              idx === queueIndex
                ? { ...item, source_id: source.id, progress: 100, status: 'completed' }
                : item
            )
          );

          toast.success(`Uploaded ${file.name}`);
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Upload failed';
          setQueue((prev) =>
            prev.map((item, idx) =>
              idx === queueIndex
                ? { ...item, progress: 0, status: 'failed', error: message }
                : item
            )
          );
          toast.error(`Failed to upload ${file.name}`);
        }
      }

      onUploadComplete?.();
    },
    [folderId, onUploadComplete, uploadSource]
  );

  const onDropRejected = useCallback(
    (rejections: readonly { file: File; errors: readonly { message: string }[] }[]) => {
      for (const rejection of rejections) {
        const errorMsg = rejection.errors.map((e) => e.message).join(', ');
        toast.error(`${rejection.file.name}: ${errorMsg}`);
      }
    },
    []
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject, open } = useDropzone({
    onDrop,
    onDropRejected,
    accept: ACCEPTED_TYPES,
    maxFiles,
    maxSize: maxSizeMb * 1024 * 1024,
    disabled: isUploading,
    noClick: true,
  });

  const removeFromQueue = (index: number) => {
    setQueue((prev) => prev.filter((_, i) => i !== index));
  };

  const clearCompleted = () => {
    setQueue((prev) => prev.filter((item) => item.status !== 'completed'));
  };

  const completedCount = queue.filter((item) => item.status === 'completed').length;

  return (
    <div className={cn('space-y-4', className)}>
      {/* Dropzone */}
      <div
        {...getRootProps()}
        onClick={open}
        className={cn(
          'group relative flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-8 transition-colors',
          isDragActive && !isDragReject && 'border-indigo-400 bg-indigo-50/50',
          isDragReject && 'border-rose-400 bg-rose-50/50',
          !isDragActive && !isDragReject && 'border-slate-200 hover:border-indigo-300 hover:bg-slate-50/50',
          isUploading && 'pointer-events-none opacity-60'
        )}
      >
        <input {...getInputProps()} />

        <div
          className={cn(
            'flex h-12 w-12 items-center justify-center rounded-full transition-colors',
            isDragActive && !isDragReject && 'bg-indigo-100',
            isDragReject && 'bg-rose-100',
            !isDragActive && 'bg-slate-100 group-hover:bg-indigo-50'
          )}
        >
          <Upload
            className={cn(
              'h-6 w-6 transition-colors',
              isDragActive && !isDragReject && 'text-indigo-500',
              isDragReject && 'text-rose-500',
              !isDragActive && 'text-slate-400 group-hover:text-indigo-400'
            )}
          />
        </div>

        <div className="mt-4 text-center">
          {isDragReject ? (
            <p className="text-sm font-medium text-rose-600">
              Unsupported file type
            </p>
          ) : isDragActive ? (
            <p className="text-sm font-medium text-indigo-600">
              Drop files here…
            </p>
          ) : (
            <>
              <p className="text-sm font-medium text-slate-700">
                Drag & drop files here, or{' '}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); open(); }}
                  className="text-indigo-600 underline underline-offset-2 hover:text-indigo-700"
                >
                  browse
                </button>
              </p>
              <p className="mt-1 text-xs text-slate-400">
                PDF, DOCX, or TXT — up to {maxSizeMb}MB each, max {maxFiles} files
              </p>
            </>
          )}
        </div>
      </div>

      {/* Upload queue */}
      {queue.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-slate-500">
              {isUploading
                ? 'Uploading…'
                : `${completedCount}/${queue.length} uploaded`}
            </p>
            {completedCount > 0 && !isUploading && (
              <Button
                variant="ghost"
                size="sm"
                className="h-auto px-2 py-1 text-xs text-slate-400 hover:text-slate-600"
                onClick={clearCompleted}
              >
                Clear completed
              </Button>
            )}
          </div>

          <div className="space-y-1.5">
            {queue.map((item, index) => {
              const iconCfg = fileIconMap[
                item.file_name.endsWith('.pdf')
                  ? 'application/pdf'
                  : item.file_name.endsWith('.docx')
                    ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                    : 'text/plain'
              ] ?? { icon: File, color: 'text-slate-400' };
              const ItemIcon = iconCfg.icon;

              return (
                <div
                  key={`${item.file_name}-${index}`}
                  className="flex items-center gap-3 rounded-lg border border-slate-100 bg-white px-3 py-2"
                >
                  <ItemIcon className={cn('h-4 w-4 shrink-0', iconCfg.color)} />

                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-medium text-slate-700">
                      {item.file_name}
                    </p>
                    {item.status === 'uploading' && (
                      <Progress value={item.progress} className="mt-1 h-1" />
                    )}
                    {item.error && (
                      <p className="mt-0.5 truncate text-[10px] text-rose-500">
                        {item.error}
                      </p>
                    )}
                  </div>

                  <div className="flex shrink-0 items-center">
                    {item.status === 'uploading' && (
                      <Loader2 className="h-4 w-4 animate-spin text-indigo-500" />
                    )}
                    {item.status === 'extracting' && (
                      <Loader2 className="h-4 w-4 animate-spin text-amber-500" />
                    )}
                    {item.status === 'completed' && (
                      <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                    )}
                    {item.status === 'failed' && (
                      <AlertCircle className="h-4 w-4 text-rose-500" />
                    )}
                  </div>

                  {(item.status === 'completed' || item.status === 'failed') && (
                    <button
                      type="button"
                      className="shrink-0 rounded p-0.5 text-slate-300 hover:text-slate-500"
                      onClick={() => removeFromQueue(index)}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
